// Free-gift logic for the cart (drawer and page). Pure functions plus one
// async reconcile step; the Alpine cart store calls them and renders the result.
// The backend Functions (price to zero, checkout validation) enforce the same rules.
(function (root) {
  // Line item property that marks a promotional gift line; its value is the campaign id.
  const marker = "_grivel_gift";
  // Cart attribute set when the customer chooses "Continue without a gift".
  const declinedAttribute = "_grivel_gift_declined";
  // True for any cart line carrying the gift marker (whatever its value or price).
  const isGift = (item) =>
    Object.prototype.hasOwnProperty.call(item.properties || {}, marker);
  // Cart attribute naming the campaign this cart qualifies for. The checkout Functions cannot read
  // the customer's tag, so they act only on the campaign named here: the theme sets it once the
  // customer is in the audience (see docs/gifts.md).
  const campaignAttribute = "_grivel_gift_campaign";
  const audiences = ["everyone", "tag"];
  const datePattern = /^\d{4}-\d{2}-\d{2}$/;

  // Whether this customer qualifies: they are in the campaign's audience. `customer` is what the theme knows about
  // the signed-in customer ({ tags }), or null for a guest.
  //   everyone          anyone, signed in or not
  //   tag               a signed-in customer carrying any one of the campaign's tags (case-insensitive)
  // Checkout checks the tag again (the validation Function), so this is the cart's early answer, not the only check.
  function audienceMet(campaign, customer) {
    const audience = campaign.audience;
    if (audience.type === "everyone") return true;
    if (!customer) return false;
    const wanted = audience.tags.map((tag) => tag.trim().toLowerCase());
    return (customer.tags || []).some((tag) =>
      wanted.includes(String(tag).trim().toLowerCase()),
    );
  }

  // Date formatters by timezone: building one is slow, and the cart asks for the date on every render.
  const dateFormats = new Map();
  // Returns the current calendar date (YYYY-MM-DD) in the shop's timezone, so campaign
  // phases switch at shop midnight rather than the browser's. Returns "" if the timezone is invalid.
  function shopDate(config, now = new Date()) {
    try {
      let format = dateFormats.get(config.timezone);
      if (!format) {
        format = new Intl.DateTimeFormat("en", {
          timeZone: config.timezone,
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        });
        dateFormats.set(config.timezone, format);
      }
      const parts = format.formatToParts(now);
      const part = (type) => parts.find((p) => p.type === type).value;
      return `${part("year")}-${part("month")}-${part("day")}`;
    } catch {
      return "";
    }
  }

  // Shopify's clock, learned from the Date header of cart responses. The checkout Functions use
  // Shopify's date, so the cart must not trust the device clock, which can be off by hours or days
  // (a gift the cart adds on the wrong day is one checkout rejects).
  //   observe(header)   records the offset between a response's Date header and the device clock
  //   now()             Shopify's current time, or undefined until a response has been seen
  function createClock() {
    let offset = null;
    return {
      observe(header, receivedAt = Date.now()) {
        const serverTime = Date.parse(header || "");
        // The header is rounded down to the second, so the server's time is half a second later on average.
        if (Number.isFinite(serverTime)) offset = serverTime + 500 - receivedAt;
      },
      now(current = Date.now()) {
        return offset === null ? undefined : new Date(current + offset);
      },
    };
  }

  // The same checks as validCampaign in the backend's functions/gifts.ts: a campaign the checkout
  // Functions would reject must not get gifts in the cart, or checkout blocks every one of them.
  const validCampaign = (c) => {
    if (
      !c ||
      typeof c.id !== "string" ||
      !/^[a-zA-Z0-9_-]{1,64}$/.test(c.id) ||
      typeof c.start !== "string" ||
      !datePattern.test(c.start) ||
      typeof c.end !== "string" ||
      !datePattern.test(c.end) ||
      !(c.start < c.end) ||
      !audiences.includes(c.audience?.type) ||
      (c.audience.type === "tag" &&
        !(
          Array.isArray(c.audience.tags) &&
          c.audience.tags.length >= 1 &&
          c.audience.tags.length <= 10 &&
          c.audience.tags.every((t) => typeof t === "string" && t.trim())
        )) ||
      !Array.isArray(c.tiers) ||
      c.tiers.length < 1 ||
      c.tiers.length > 10
    )
      return false;
    // Spend needed per currency (3-letter codes): every gift has the same currencies (the first
    // gift's), rising.
    const first = c.tiers[0]?.thresholds;
    const currencies =
      first && typeof first === "object" ? Object.keys(first) : [];
    if (
      currencies.length < 1 ||
      !currencies.every((code) => /^[A-Z]{3}$/.test(code))
    )
      return false;
    const previous = {};
    const variants = new Set();
    for (const t of c.tiers) {
      if (
        !t ||
        typeof t.variantId !== "string" ||
        !/^\d+$/.test(t.variantId) ||
        !t.name ||
        !t.thresholds ||
        typeof t.thresholds !== "object" ||
        Object.keys(t.thresholds).length !== currencies.length ||
        variants.has(t.variantId)
      )
        return false;
      for (const currency of currencies) {
        const value = t.thresholds[currency];
        if (!Number.isSafeInteger(value) || value <= (previous[currency] ?? 0))
          return false;
        previous[currency] = value;
      }
      variants.add(t.variantId);
    }
    return true;
  };

  // The campaign running today in the shop's timezone (start included, end excluded), or null.
  // Only that campaign is validated; the admin refuses overlapping or duplicate campaigns.
  function activeCampaign(config, now) {
    if (config?.version !== 3 || !config.timezone) return null;
    if (!Array.isArray(config.campaigns)) return null;
    const date = shopDate(config, now);
    if (!date) return null;
    const campaign = config.campaigns.find(
      (c) => c && date >= c.start && date < c.end,
    );
    return validCampaign(campaign) ? campaign : null;
  }

  // Whether a cart line is a gift the backend makes free (the Cart Transform's rule): marked with the
  // running campaign, one of its gift variants, quantity 1, no subscription. While the cart swaps one
  // gift for another, the old line still is one until it is removed, so its label stays; it is not
  // the same as the gift the cart has earned (`awarded`).
  function isFreeGift(campaign, item) {
    return (
      !!campaign &&
      isGift(item) &&
      item.properties[marker] === campaign.id &&
      item.quantity === 1 &&
      !item.selling_plan_allocation &&
      campaign.tiers.some((t) => t.variantId === String(item.variant_id))
    );
  }

  // Derives everything the UI and reconcile need from the config, the Ajax cart and the customer:
  //   campaign          the campaign running today (null when none)
  //   tiers             its gifts with `threshold` in the cart's currency, lowest first
  //   visible/eligible  the cart may show/receive gifts from that campaign
  //   spend             discounted merchandise value in cents, excluding gift lines
  //   tier              the gift this spend earns (null if none or declined)
  //   next              the next tier still to unlock, for "spend X more" messages
  //   awarded           exactly one correct gift line is already in the cart
  // Any invalid config, date, currency or cart returns the empty state (no gift).
  function state(config, cart, now, customer) {
    const gifts = (cart?.items || []).filter(isGift);
    // Returned whenever no campaign applies or it is misconfigured: nothing shown, nothing added.
    const empty = {
      campaign: null,
      campaignAttribute: "",
      attributeStale: (cart?.attributes?.[campaignAttribute] || "") !== "",
      visible: false,
      eligible: false,
      tiers: [],
      tier: null,
      next: null,
      gifts,
      spend: 0,
      awarded: false,
    };
    const campaign = activeCampaign(config, now);
    // Each gift has a threshold per currency; a cart in a currency the campaign has no amounts for
    // gets no gift.
    if (
      !campaign ||
      !cart ||
      campaign.tiers[0].thresholds[cart.currency] === undefined
    )
      return empty;
    const tiers = campaign.tiers.map((t) => ({
      ...t,
      threshold: t.thresholds[cart.currency],
    }));
    // Who qualifies is the campaign's audience (everyone, or customers with a tag).
    const eligible = audienceMet(campaign, customer);
    // Discounted merchandise value: paid lines after product discounts, less order-level discounts.
    // Gift lines are skipped by their marker alone: these rules never read or react to a gift's
    // price. Making the gift free is the backend's job (Cart Transform, checkout validation).
    const spend =
      cart.items.reduce(
        (total, item) => total + (isGift(item) ? 0 : item.final_line_price),
        0,
      ) -
      (cart.cart_level_discount_applications || []).reduce(
        (total, d) => total + (d.total_allocated_amount || 0),
        0,
      );
    if (!Number.isSafeInteger(spend) || spend < 0) return empty;
    const declined = cart.attributes?.[declinedAttribute] === campaign.id;
    // Highest tier whose threshold the spend reaches (tiers are ordered low to high).
    const selected = [...tiers].reverse().find((t) => spend >= t.threshold);
    const tier = eligible && !declined && selected ? selected : null;
    // Awarded when exactly one correct gift line is present (its price is not our concern).
    const awarded =
      !!tier &&
      gifts.length === 1 &&
      String(gifts[0].variant_id) === tier.variantId &&
      gifts[0].properties[marker] === campaign.id &&
      gifts[0].quantity === 1 &&
      !gifts[0].selling_plan_allocation;
    // The campaign the Functions should act on: set only while this customer can really get a gift.
    const wanted = eligible && !declined ? campaign.id : "";
    return {
      campaign,
      campaignAttribute: wanted,
      attributeStale: (cart.attributes?.[campaignAttribute] || "") !== wanted,
      visible: eligible,
      eligible,
      declined,
      gifts,
      tiers,
      tier,
      spend,
      awarded,
      next: tiers.find((t) => spend < t.threshold) || null,
    };
  }

  // Remembers the last state() and computes it again only when the config, cart or customer object,
  // or the shop date, changes. The cart store reads the state from many bindings per render. The
  // cart is replaced, never changed in place, when it updates, so comparing objects is enough.
  // Formatting the shop date is the slow part, so it is worked out once per minute and timezone:
  // every timezone's midnight falls on a whole minute.
  function createStateCache() {
    let last = null;
    let day = null;
    return (config, cart, now = new Date(), customer) => {
      const timezone = config?.timezone || "";
      const minute = Math.floor(now.getTime() / 60000);
      if (!day || day.timezone !== timezone || day.minute !== minute) {
        day = {
          timezone,
          minute,
          date: timezone ? shopDate(config, now) : "",
        };
      }
      const date = day.date;
      if (
        last &&
        last.config === config &&
        last.cart === cart &&
        last.customer === customer &&
        last.date === date
      )
        return last.value;
      last = {
        config,
        cart,
        customer,
        date,
        value: state(config, cart, now, customer),
      };
      return last.value;
    };
  }

  // Runs one gift sync at a time. A plain sync reuses the one in progress. A forced one (checkout,
  // retry, decline) must read the cart itself and run its own decline, so it waits for the one in
  // progress and then runs. `start` returns the sync's promise. A sync that fails frees the queue
  // like one that succeeds, and a forced sync waiting on it still runs.
  // `blocked` says whether other cart updates (quantities, removals, adds) are still queued. A plain
  // sync then waits and runs once, after the last of them: the caller calls resume() whenever one
  // finishes. Without this, a gift round would run between each of several quick changes. Forced
  // syncs don't wait here; they queue behind those updates in the cart's own mutation queue.
  function createSyncQueue(blocked = () => false) {
    let current = null;
    let waiting = null;
    const queue = {
      run(force, start) {
        if (current) {
          if (!force) return current;
          const next = () => queue.run(force, start);
          return current.then(next, next);
        }
        if (!force && blocked()) {
          // Several plain syncs while updates are queued become one, using the latest `start`.
          if (!waiting) {
            let resolve;
            const promise = new Promise((done) => (resolve = done));
            waiting = { promise, resolve };
          }
          waiting.start = start;
          return waiting.promise;
        }
        const promise = start();
        // A sync that starts while one is waiting (a forced one, or a plain one once the cart is idle)
        // reads the newer cart, so it answers the waiting one too.
        if (waiting) {
          waiting.resolve(promise);
          waiting = null;
        }
        current = promise;
        const done = () => {
          if (current === promise) current = null;
        };
        promise.then(done, done);
        return promise;
      },
      // Runs the waiting plain sync once no cart update is queued any more.
      resume() {
        if (!waiting || blocked()) return;
        const { start, resolve } = waiting;
        waiting = null;
        resolve(queue.run(false, start));
      },
    };
    return queue;
  }

  // Compact fingerprint of the cart fields that affect gifts, so callers can skip
  // re-running reconcile when nothing relevant has changed. Shopify gives every discount in
  // every cart response a fresh random `key` and a `created_at` timestamp, and may rehash line
  // keys, so only stable values (titles, amounts, variants, quantities, prices) are used.
  // Anything volatile here makes each refetch look like a new cart and retries the gift forever.
  function signature(cart) {
    const discount = (d) => [d?.title, d?.total_allocated_amount];
    return JSON.stringify([
      cart?.currency,
      cart?.attributes,
      cart?.cart_level_discount_applications?.map(discount),
      cart?.items?.map((i) => [
        i.variant_id,
        i.quantity,
        isGift(i) ? null : i.final_line_price,
        i.properties,
        i.line_level_discount_allocations?.map((a) => [
          a.amount,
          a.discount_application?.title,
        ]),
      ]),
    ]);
  }

  // Brings the cart's gift line in line with state(): starts from `cart` when the caller already has
  // the current cart (saving a request), otherwise fetches it, then runs up to 5 rounds of removing
  // wrong/extra gifts or adding the earned gift.
  // `api` supplies getCart/updateCart/addToCart; `onCart` receives the cart for the UI;
  // `decline` records that the customer opted out; `accept` undoes that. Returns the final cart, or throws
  // "gift_update" if it does not settle.
  // Adding a gift makes Shopify re-check the whole cart (e.g. stock), which can lower the
  // qualifying spend. So the cart is published once it has settled, never while a gift that was
  // just added may still be removed; customers never see a gift that appears and disappears.
  async function reconcile(
    api,
    config,
    {
      onCart = () => {},
      decline = false,
      accept = false,
      now,
      customer,
      cart: current,
    } = {},
  ) {
    let cart = current ?? (await api.getCart());
    onCart(cart);
    const published = cart;
    try {
      // "Undo" after Continue without a gift: forget the choice, so the earned gift is added again.
      if (accept && cart.attributes?.[declinedAttribute]) {
        cart = await api.updateCart({
          attributes: { [declinedAttribute]: "" },
        });
      }
      const declining = activeCampaign(config, now);
      if (decline && declining) {
        cart = await api.updateCart({
          attributes: { [declinedAttribute]: declining.id },
        });
      }
      for (let attempt = 0; attempt < 5; attempt++) {
        const current = state(config, cart, now, customer);
        const keep = current.gifts.find(
          (i) =>
            current.tier &&
            String(i.variant_id) === current.tier.variantId &&
            i.properties[marker] === current.campaign.id &&
            i.quantity === 1 &&
            !i.selling_plan_allocation,
        );
        // One update.js call both tells the checkout Functions which campaign this cart qualifies
        // for (or none) and removes wrong/extra gifts; it always comes before any add. Neither
        // changes the spend (gift lines do not count), so the earned gift stays the same.
        const remove = current.gifts.filter((i) => i !== keep);
        if (current.attributeStale || remove.length) {
          cart = await api.updateCart({
            ...(current.attributeStale && {
              attributes: { [campaignAttribute]: current.campaignAttribute },
            }),
            ...(remove.length && {
              updates: Object.fromEntries(remove.map((i) => [i.key, 0])),
            }),
          });
          continue;
        }
        // A correct gift stays whatever its price: the backend makes it free (Cart Transform) and
        // rejects any marked gift that is not (checkout validation).
        if (keep) return cart;
        if (!current.tier) return cart;
        await api.addToCart({
          items: [
            {
              id: current.tier.variantId,
              quantity: 1,
              properties: { [marker]: current.campaign.id },
            },
          ],
        });
        cart = await api.getCart();
      }
      throw new Error("gift_update");
    } finally {
      if (cart !== published) onCart(cart);
    }
  }

  // Public surface, shared with tests (CommonJS) and the cart store (window.Gifts).
  const api = {
    marker,
    declinedAttribute,
    campaignAttribute,
    isGift,
    isFreeGift,
    activeCampaign,
    audienceMet,
    shopDate,
    createClock,
    clock: createClock(),
    createStateCache,
    createSyncQueue,
    state,
    signature,
    reconcile,
  };
  if (typeof module !== "undefined" && module.exports) module.exports = api;
  else root.Gifts = api;
})(typeof window !== "undefined" ? window : globalThis);
